import * as React from 'react';
import { connect } from 'react-redux';
import { IContextualMenuItem, arraysEqual, DayOfWeek } from 'office-ui-fabric-react';
import { ApplicationState } from '../../../../store';
import { getFirstDayOfWeekSetting } from '../../../../store/selectors/settingsSelectors';
import { IListProps } from '../../../../components/common/extensibleEntity/EntityDetailsList';
import { TimelineGroupingProps, IRow, TimelineChange } from '../../../../components/common/timeline/TimelineList';
import { IExtensibleEntity, Dictionary, Quantization, ITimeframe, updateCheckboxOptions, IScalableTimeframe } from '../../../../entities/common';
import ListMenu, { OwnProps as ListMenuProps } from '../../../../components/common/extensibleEntity/ListMenu';
import { TimelineScale, IScale } from '../../../../components/common/timeline/TimelineScale';
import { ISelectableCellData } from '../ResourceUsageCell';
import MarqueeSelectionWrapper from '../../../../components/common/MarqueeSelectionWrapper';
import EntityTimelineList, { ITimelineProps } from '../../../../components/common/extensibleEntity/EntityTimelineList';
import { ScaleRenderMode } from '../../../../components/common/timeline/TimelineBody';
import { IScaleTimelineSegment, ITimelineSegment } from '../../../../components/common/timeline/TimelineSegment';
import { RowMenu } from '../../../../components/common/extensibleEntity/RowMenu';
import SelectionExt from '../../../common/SelectionExt';
import { notUndefined } from '../../../utils/common';
import { Field } from '../../../../entities/Metadata';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { ControlSpiner } from '../../../common/Spinner';

const duration_days = 210;
export const defaultTimeframe: ITimeframe = {
    start: new Date().getMonthStartDate(),
    end: new Date().getMonthStartDate().addDays(duration_days).getMonthEndDate()
}

export const SCALE_MULTIPLIER_PER_TIMETYPE_COLUMN = 1.5;

export type SegmentContentProps = {
    row: IRow;
    segment: IScaleTimelineSegment;
    cellSelection: SelectionExt;
    selectionIndex?: number;
    quantization: Quantization;
    componentRef?: React.LegacyRef<HTMLDivElement>;
    onKeyDown?: React.KeyboardEventHandler<Element>;
    onKeyPress?: React.KeyboardEventHandler<Element>;
    onClick?: React.MouseEventHandler<Element>;
    onEditStart?: () => void;
    onEditCompleteBuilder: <F extends (...args: any[]) => void>(fn: F) => F | undefined;
};

export type MenuRenderProps = { cells: ISelectableCellData[]; quantization: Quantization; };
export type TooltipRenderProps = { entity: IExtensibleEntity; segment: ITimelineSegment; quantization: Quantization; };

export type OwnProps = {
    scrollable?: boolean;
    entitiesIds: string[];
    selectedEntityIds?: string[];
    isLoading: boolean;
    menuProps?: ListMenuProps;
    listProps: IListProps & Partial<ITimelineProps>;
    timelineGrouping?: TimelineGroupingProps;
    isEditable?: ((entity: IExtensibleEntity) => boolean);
    renderSegmentContent: (props: SegmentContentProps) => JSX.Element | undefined;
    renderCellMenu?: (props: MenuRenderProps) => JSX.Element;
    renderTooltip?: (props: TooltipRenderProps) => JSX.Element | undefined;
    buildRowMenuItems?: (entity: IExtensibleEntity) => IContextualMenuItem[];
    onTimeframeChanged?: (timeframe: ITimeframe) => void;

    timeTypeLength: number;
}

type StoreProps = {
    firstDayOfWeek: DayOfWeek;
}
type Props = OwnProps & StoreProps & RouteComponentProps<{}>;
type CellIndexesByRowByDateMap = Dictionary<Dictionary<number>>;

interface State {
    cellIndexMap: CellIndexesByRowByDateMap;
    cellsInRow: number;
    totalCells: number;
    quantization: Quantization;
    timeframe: ITimeframe;
}

class BaseUsageGrid extends React.Component<Props, State> {
    private readonly _cellSelection: SelectionExt;
    private _cellByIndexMap: Dictionary<any> = {};
    private _cellPositionToIndexMap: Dictionary<number | undefined> = {};
    private _isCellSelectionStarted: boolean = false;
    private _isCellEditingStarted: boolean = false;

    constructor(props: Props) {
        super(props);
        const timeframe = { ...defaultTimeframe, ...props.listProps.userTimeframe };

        const scale = TimelineScale.build(timeframe, props.listProps.userQuantization, props.firstDayOfWeek);
        const cellIndexMap = this._buildCellIndexMap(props.entitiesIds, scale.scale);
        this._cellSelection = new SelectionExt({
            onSelectionChanged: () => {
                if (this.props.selectedEntityIds?.length) {
                    this.props.listProps.selection?.setAllSelected(false)
                }
            }
        });
        this._cellSelection.setItems(cellIndexMap.endToEndItems, true);

        this.state = {
            cellIndexMap: cellIndexMap.map,
            cellsInRow: Object.keys(cellIndexMap.map).length > 0 ? Object.keys(cellIndexMap.map[Object.keys(cellIndexMap.map)[0]]).length : 0,
            totalCells: cellIndexMap.endToEndItems.length,
            quantization: scale.quantization,
            timeframe
        };
    }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.selectedEntityIds && (!this.props.selectedEntityIds || !arraysEqual(nextProps.selectedEntityIds, this.props.selectedEntityIds))) {
            this._cellSelection.setAllSelected(false);
        }

        const newEntitiesIds = nextProps.entitiesIds;
        const oldEntitiesIds = this.props.entitiesIds;
        if (!arraysEqual(oldEntitiesIds, newEntitiesIds)) {
            const scale = TimelineScale.build(this.state.timeframe, this.state.quantization, nextProps.firstDayOfWeek, nextProps.timeTypeLength);
            const cellIndexMap = this._buildCellIndexMap(newEntitiesIds, scale.scale);
            this.setState({
                cellIndexMap: cellIndexMap.map,
                cellsInRow: Object.keys(cellIndexMap.map).length > 0 ? Object.keys(cellIndexMap.map[Object.keys(cellIndexMap.map)[0]]).length : 0,
                totalCells: cellIndexMap.endToEndItems.length
            });
            this._cellSelection.setItems(cellIndexMap.endToEndItems, true);
        }
    }

    render() {
        const { timelineGrouping, isEditable, menuProps, isLoading } = this.props;
        const { quantization, cellsInRow, totalCells } = this.state;

        const isWholeGridReadOnly = isEditable === undefined;

        const listProps: IListProps & Partial<ITimelineProps> = this._buildListProps();

        let cellPosition = 0;
        return <ControlSpiner isLoading={isLoading} className="show-over">
            {menuProps && <ListMenu {...menuProps} />}
            <MarqueeSelectionWrapper selection={this._cellSelection} isDraggingConstrainedToRoot={true}
                isEnabled={!isWholeGridReadOnly}
                onSelectionStarted={() => {
                    this._isCellSelectionStarted = true;
                    this._clearRectangle();
                }}
                onSelectionStopped={() => {
                    this._isCellSelectionStarted = false;
                }}
                rootProps={this.props.scrollable ? { 'data-is-scrollable': true } as any : undefined}
                className="entities-list-body marquee-root"
            >
                <EntityTimelineList key="timeline"
                    {...listProps}
                    {...updateCheckboxOptions(listProps, menuProps?.selectionMode?.items, true)}
                    buildRow={listProps.buildRow!}
                    initialTimeframe={defaultTimeframe}
                    userQuantization={quantization}
                    scaleRenderMode={ScaleRenderMode.Cell}
                    timelineGrouping={timelineGrouping}
                    onScaleChange={this._onScaleChange}
                    renderSegmentContent={(row: IRow, segment: IScaleTimelineSegment) => {
                        const idx = isEditable?.(row.entity) ? this.state.cellIndexMap[row.key]?.[segment.startDate.getTime()] : undefined;
                        cellPosition++;
                        const position = cellPosition;
                        this._cellPositionToIndexMap[position] = idx;

                        const renderProps: SegmentContentProps = {
                            row: row,
                            segment: segment,
                            cellSelection: this._cellSelection,
                            selectionIndex: idx,
                            componentRef: idx !== undefined ? (_) => _ && (this._cellByIndexMap[idx] = _) : undefined,
                            onClick: idx !== undefined
                                ? (e) => {
                                    e.stopPropagation();
                                    this._clearRectangle();
                                    this._navigateToCellByIndex(idx, e.shiftKey);
                                }
                                : undefined,
                            onKeyDown: idx !== undefined
                                ? (e) => {
                                    let nextPosition = undefined;
                                    if (e.key === 'ArrowLeft') {
                                        nextPosition = position - 1;
                                    } else if (e.key === 'ArrowRight') {
                                        nextPosition = position + 1;
                                    } else if (e.key === 'ArrowUp') {
                                        nextPosition = position - cellsInRow;
                                    } else if (e.key === 'ArrowDown') {
                                        nextPosition = position + cellsInRow;
                                    }

                                    if (nextPosition !== undefined && nextPosition < totalCells && this._cellPositionToIndexMap[nextPosition] !== undefined) {
                                        if (!e.shiftKey) {
                                            this._clearRectangle();
                                            this._navigateToCellByIndex(this._cellPositionToIndexMap[nextPosition]!, e.shiftKey);
                                        }
                                        else {
                                            this._selectRectangle(nextPosition, position, cellsInRow);
                                        }
                                        e.preventDefault();
                                    }

                                    if (e.key === 'Tab') {
                                        const _nextPosition = e.shiftKey ? position - 1 : position + 1;
                                        if (_nextPosition !== undefined && _nextPosition < totalCells && this._cellPositionToIndexMap[_nextPosition] !== undefined) {
                                            this._clearRectangle();
                                            this._navigateToCellByIndex(this._cellPositionToIndexMap[_nextPosition]!, false);
                                            e.preventDefault();
                                        }
                                    }
                                }
                                : undefined,
                            onKeyPress: idx !== undefined
                                ? (e) => {
                                    if (e.key === 'Enter' && this._isCellEditingStarted) {
                                        this._cellByIndexMap[idx]?.focus();
                                    }
                                }
                                : undefined,
                            onEditStart: idx !== undefined ? () => {
                                this._isCellEditingStarted = true;
                            } : undefined,
                            onEditCompleteBuilder: <F extends (...args: any[]) => void>(fn: F): F | undefined => {
                                if (idx === undefined) {
                                    return undefined;
                                }
                                return ((...args: any[]) => {
                                    this._isCellEditingStarted = false;
                                    fn(...args);
                                }) as F;
                            },
                            quantization: quantization,
                        };
                        return this.props.renderSegmentContent(renderProps);
                    }}
                    renderSegmentTooltipContent={(row, segment) => {
                        if (this._isCellSelectionStarted || this._isCellEditingStarted) {
                            return undefined;
                        }

                        const selectedCells = this._cellSelection.getSelectedIndices();
                        const idx = this.state.cellIndexMap[row.key]?.[segment.startDate.getTime()];

                        if (selectedCells.length > 1 && idx !== undefined && !!~selectedCells.indexOf(idx)) {
                            const selectedCells = this._cellSelection.getSelection().map(_ => _ as ISelectableCellData);

                            const menuRenderProps: MenuRenderProps = {
                                cells: selectedCells,
                                quantization: this.state.quantization
                            }
                            return this.props.renderCellMenu?.(menuRenderProps);
                        }

                        const tooltipProps: TooltipRenderProps = {
                            entity: row.entity,
                            segment: segment,
                            quantization: this.state.quantization
                        }
                        return this.props.renderTooltip?.(tooltipProps);
                    }}
                    onItemMenuRender={item => {
                        const commands = this.props.buildRowMenuItems?.(item) || [];
                        return commands.length
                            ? <RowMenu
                                commands={commands}
                                selection={this.props.listProps.selection}
                                item={item}
                            />
                            : null;
                    }}
                />
            </MarqueeSelectionWrapper>
        </ControlSpiner>;
    }

    private _onScaleChange = (timelineChange: TimelineChange) => {
        const { timeframe } = this.state;
        const { newState, origin } = timelineChange;
        const { quantization, scale } = newState;

        const newTimeline = {
            start: origin?.timeframe?.start || scale.dates[0].start,
            end: origin?.timeframe?.end || scale.dates[scale.dates.length - 1].finish
        };

        const isTimeframeChanged = timeframe.start.getBeginOfDay().getTime() !== newTimeline.start.getBeginOfDay().getTime()
            || timeframe.end.getBeginOfDay().getTime() !== newTimeline.end.getBeginOfDay().getTime();

        if (isTimeframeChanged || quantization !== this.state.quantization) {
            if (isTimeframeChanged) {
                const newTimeframe = { start: newTimeline.start.getBeginOfDay(), end: newTimeline.end.getBeginOfDay() };
                this.setState({
                    timeframe: newTimeframe
                });
                this.props.onTimeframeChanged?.(newTimeframe);
            }

            const { map, endToEndItems } = this._buildCellIndexMap(this.props.entitiesIds, scale);
            this.setState({
                cellIndexMap: map,
                cellsInRow: Object.keys(map).length > 0 ? Object.keys(map[Object.keys(map)[0]]).length : 0,
                totalCells: endToEndItems.length,
                quantization
            });

            this._cellSelection.setItems(endToEndItems, true);
            this._cellByIndexMap = {};
            this._cellPositionToIndexMap = {};
            this._clearRectangle();
        }
        this.props.listProps.onScaleChange?.(timelineChange);
    }

    private _buildListProps = (): IListProps & Partial<ITimelineProps> => {
        return {
            ...this.props.listProps,
            scaleMultiplier: this.props.timeTypeLength + SCALE_MULTIPLIER_PER_TIMETYPE_COLUMN,
            isFieldFake: (field: Field) => this.props.listProps.isFieldFake?.(field) || false,
            onItemRender: this.props.listProps.onItemRender
                ? (item, index, field, defaultRender) =>
                    this.props.listProps.onItemRender!(item, index, field, defaultRender)
                : undefined,
        };
    }

    private _buildCellIndexMap(entitiesIds: string[], scale: IScale): { map: CellIndexesByRowByDateMap, endToEndItems: ISelectableCellData[] } {
        return entitiesIds.map((entityId, rowIndex) => ({
                entityId,
                datesWithIndex: scale.dates.map((_, index) => ({ date: _, endToEndIndex: rowIndex * scale.dates.length + index }))
            }))
            .reduce<{ map: CellIndexesByRowByDateMap, endToEndItems: ISelectableCellData[] }>((prev, cur) => ({
                map: {
                    ...prev.map,
                    [cur.entityId]: cur.datesWithIndex.reduce((_prev, _cur) => ({ ..._prev, [_cur.date.start.getTime()]: _cur.endToEndIndex }), {})
                },
                endToEndItems: [
                    ...prev.endToEndItems,
                    ...cur.datesWithIndex.map<ISelectableCellData>(_ => ({ key: _.endToEndIndex, entityId: cur.entityId, start: _.date.start, finish: _.date.finish }))]
            }), { map: {}, endToEndItems: [] });
    }

    private _navigateToCellByIndex = (idx: number, preserveSelection: boolean) => {
        this._cellByIndexMap[idx]?.focus();
        this._selectCellByIndex(idx, preserveSelection);
    }

    private _rectangleStartPosition?: number;
    private _prevRectangleSelectedIdx?: number[]

    private _clearRectangle = () => {
        this._rectangleStartPosition = undefined;
        this._prevRectangleSelectedIdx = undefined;
    }

    private _selectRectangle = (position: number, prevPosition: number, itemsInRow: number) => {
        const cellIndexToFocus = this._cellPositionToIndexMap[position];
        if (cellIndexToFocus !== undefined) {
            this._cellByIndexMap[cellIndexToFocus]?.focus();
        }

        this._rectangleStartPosition ??= prevPosition;

        const indexes = this._calculateRectangle(position, this._rectangleStartPosition, itemsInRow)
            .map(_ => this._cellPositionToIndexMap[_])
            .filter(notUndefined);

        this._cellSelection.setChangeEvents(false);
        this._prevRectangleSelectedIdx?.forEach(_ => {
            this._cellSelection.setIndexSelected(_, false, true);
        });
        this._prevRectangleSelectedIdx = [];
        indexes.forEach(_ => {
            if (!this._cellSelection.isIndexSelected(_)) {
                this._prevRectangleSelectedIdx?.push(_);
                this._cellSelection.setIndexSelected(_, true, true);
            }
        });
        this._cellSelection.setChangeEvents(true);
    }

    private _calculateRectangle = (positionStart: number, positionEnd: number, itemsInRow: number): number[] => {
        const result: number[] = [];

        const leftTopCorner = Math.floor(Math.min(positionStart, positionEnd) / itemsInRow) * itemsInRow + Math.min(positionEnd % itemsInRow, positionStart % itemsInRow);
        const rowsCount = Math.abs(Math.floor(positionEnd / itemsInRow) - Math.floor(positionStart / itemsInRow)) + 1;
        const columnsCount = Math.abs(positionEnd % itemsInRow - positionStart % itemsInRow) + 1;

        for (let i = 0; i < rowsCount; i++) {
            for (let j = 0; j < columnsCount; j++) {
                result.push(leftTopCorner + i * itemsInRow + j)
            }
        }

        return result;
    }

    private _selectCellByIndex = (idx: number, preserveSelection: boolean) => {
        this._cellSelection.setChangeEvents(false);
        const select = !this._cellSelection.isIndexSelected(idx);
        if (!preserveSelection) {
            this._cellSelection.setAllSelected(false);
        }
        this._cellSelection.setIndexSelected(idx, select, true);
        this._cellSelection.setChangeEvents(true);
    }
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StoreProps {
    return {
        firstDayOfWeek: getFirstDayOfWeekSetting(state)
    };
}

export default withRouter<OwnProps>(connect(mapStateToProps)(BaseUsageGrid));

export const onScaleChanged = (resourceIds: string[], callback: (resourceIds: string[], timeframe: IScalableTimeframe) => void,
    timelineChange: TimelineChange): void => {
    const { prevState, newState, origin, prevOrigin } = timelineChange;

    const newTimeline = {
        start: origin?.timeframe?.start || newState.scale.dates[0].start,
        end: origin?.timeframe?.end || newState.scale.dates[newState.scale.dates.length - 1].finish
    };

    const prevTimeline = prevState
        ? {
            start: prevOrigin?.timeframe?.start || prevState.scale.dates[0].start,
            end: prevOrigin?.timeframe?.end || prevState.scale.dates[prevState.scale.dates.length - 1].finish
        }
        : undefined;

    if (!prevTimeline
        || newTimeline.start.getTime() !== prevTimeline.start.getTime()
        || newTimeline.end.getTime() !== prevTimeline.end.getTime()) {
        callback(resourceIds, convertToDateOnlyTimeframe(newTimeline));
    }
}

export const convertToDateOnlyTimeframe = (timeframe: ITimeframe): IScalableTimeframe => ({
    startDate: timeframe.start.toDateOnlyString(),
    endDate: timeframe.end.toDateOnlyString()
})